import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { HeaderProgressTracker } from '../Common/HeaderStepper/HeaderProgressTracker';
import FullDigital from './FullDigital';
import Lga from '../Common/InsuranceRequest/Lga';
import TobaccoUsage from '../Common/Helpers/TobaccoUsage';
import { saveSessionEvent } from '../../../session';
import { getBasicInfo, getSessionStorageItem, deleteConsumerApplicationId, SESSION_STORAGE_BASIC_INFO_KEY, SESSION_STORAGE_FULLY_DIGITAL_OBJECT_KEY, SESSION_STORAGE_ACCOUNT_KEY, setSessionStorageItem, SESSION_STORAGE_UTM_DATA, SESSION_STORAGE_SSO_ACCESS_TOKEN_KEY } from '../../../sessionStorage';
import { QuoteProductID } from '../Common/QuoteProductID';
import { GetHandoffPath } from '../Common/GetHandoffPath';
import GetProductDetail from '../../Common/GetProductDetail';

export default function Digital(props) {
    const navigate = useNavigate();
    const location = useLocation();
    useEffect(() => { saveSessionEvent(); }, [location]);

    const path = location ? location.pathname.split("/").pop() : null;
    const allFormState = getSessionStorageItem(SESSION_STORAGE_FULLY_DIGITAL_OBJECT_KEY);
    const basicInfo = getBasicInfo();
    const prefill = location.search && location.search.toLowerCase().includes('prefill');

    const newFormState = {
        currentStep: 1,
        pages: {
            quoter: FullDigital.DefaultFormState(prefill),
        },
    };

    const initialFormState = allFormState || newFormState;

    const [formState, setFormState] = useState(initialFormState);

    useEffect(() => {
        let productDetail = GetProductDetail(['/landing/digital-term', '/landing/term/digital-term']);
        if (productDetail === undefined) {
          productDetail = GetProductDetail(['/landing/term-insurance', '/term-insurance']);
          const redirectPath = productDetail === undefined ? '/' : '/landing/term-insurance';
          navigate(redirectPath);
        }
      }, []);    

    useEffect(() => {

        if (!path) {
            return;
        }

        let nextStep = steps.find((s) => s.path === path);

        if (!nextStep) {
            const newPath = location ? location.pathname.split("/").slice(-2) : null;
            if (!newPath) {
                return;
            }

            nextStep = steps.find((s) => s.path === newPath[0]);
            if (!nextStep) {
                return;
            }
        }
        const newFormState = { ...formState, currentStep: nextStep.step };
        setFormState(newFormState);
    }, [path]);

    const onNext = () => {
        let nextStep = steps.find((s) => s.step === formState.currentStep + 1);

        if (!nextStep) {
            throw new Error('onNext did not find a next step');
        }

        const newFormState = { ...formState, currentStep: nextStep.step };

        setFormState(newFormState);
        setSessionStorageItem(SESSION_STORAGE_FULLY_DIGITAL_OBJECT_KEY, newFormState);

        navigate(`/quote/term-life-insurance/fully-digital/${nextStep.path}`);
        window.scroll(0, 0);
    };

    const onBack = () => {
        let backStep = steps.find(s => s.step === formState.currentStep - 1);

        if (backStep) {
            const newFormState = { ...formState, currentStep: backStep.step };

            setFormState(newFormState);
            setSessionStorageItem(SESSION_STORAGE_FULLY_DIGITAL_OBJECT_KEY, newFormState);

            navigate(`/quote/term-life-insurance/fully-digital/${backStep.path}`);
            window.scroll(0, 0);

        }
        else {
            navigate('/');
        }
    };

    const logFinalDigitalQuote = (email, firstName, lastName, quoter, oneClickTypeId, referralId = null, phone = '') => {
        const account = getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY);
        const utmData = getSessionStorageItem(SESSION_STORAGE_UTM_DATA);

        let digitalQuoteData = {
            CoverageAmount: quoter.coverageAmount,
            CoverageLength: quoter.term,
            State: basicInfo.state,
            BirthDate: format(new Date(basicInfo.birthdate), 'yyyy-MM-dd'),
            HealthClass: quoter.healthClass,
            Gender: basicInfo.gender,
            Tobacco: TobaccoUsage(basicInfo.tobacco),
            Email: email,
            FirstName: firstName,
            LastName: lastName,
        };
        return new Promise(resolve => {
            //save quote log for bms
            //keep in mind that this needs to match 1click quote log format
            const requestString = JSON.stringify(digitalQuoteData);

            let jsonResponse;

            // set custom quote id in response if present 
            if (referralId) {
                const response = { ...quoter.selectedQuote };
                response.PartnerQuoteId = referralId;
                response.UTMs = utmData || "";
                jsonResponse = JSON.stringify(response);
            }
            else {
                quoter.selectedQuote.UTMs = utmData || "";
                jsonResponse = JSON.stringify(quoter.selectedQuote);
            }

            const pathName = GetHandoffPath(quoter.selectedQuote.productID);
            saveSessionEvent(true, pathName);
            //clear product session
            sessionStorage.removeItem(SESSION_STORAGE_FULLY_DIGITAL_OBJECT_KEY);
            sessionStorage.removeItem(SESSION_STORAGE_BASIC_INFO_KEY);

            const accessToken = getSessionStorageItem(SESSION_STORAGE_SSO_ACCESS_TOKEN_KEY);
            const covrServicesUrl = window.__ENV__.REACT_APP_COVR_SERVICES_URL;

            fetch(`${covrServicesUrl}/api/QuoteLog/ArchiveQuoteLog`, {
                method: 'post',
                headers: {
                'Content-Type': 'application/json',
                'SessionId': account.sessionId,
                'X-Consumer-Application-ID': account.consumerApplicationId,
                'Authorization': accessToken ? 'Bearer ' + accessToken : null,
                },
                body: JSON.stringify({
                    QuoteLogId: quoter.selectedQuote.quoteLogId,
                    OneClickTypeID: oneClickTypeId, //digital oneclick type
                    Request: requestString,
                    Response: jsonResponse,
                    ApplicationID: account.sessionId,
                    OneClickAccountID: account.account.oneClickAccountId,
                    SessionID: account.sessionId,
                    ConsumerAccountId: account.account.accountId,
                    PartnerQuoteId: referralId,
                }),
            })
            .then(deleteConsumerApplicationId)
            .then(() => { resolve(); });
        });
        
      };

    const steps = [
        {
            step: 1,
            path: 'fully-digital',
            progressTitle: 'Getting Started',
            component: <FullDigital
                formState={formState.pages.quoter}
                fullFormState={formState}
                onMultiFieldChange={(fs) => {
                    setFormState(fs);
                }}
                onNext={onNext}
                onBack={onBack}
            />,
            hideProgressTracker: true,
            showContainer: false,
        },
        {
            step: 2,
            path: 'lga',
            title: 'Your Life Insurance Request',
            progressTitle: 'Getting Started',
            component: <Lga
                formData={formState.pages}
                onBack={() => {navigate(`/quote/term-life-insurance/fully-digital`);}}
                basicInfo={basicInfo}
                logQuote={logFinalDigitalQuote}
            />,
            hideProgressTracker: true,
            hidden: false,
        },
    ];

    const currentStepObj = steps.find(s => s.step === formState.currentStep) || steps[0];

    return (
        <div>
            <Helmet>
                <title>{`Term life insurance - ${currentStepObj?.progressTitle}`}</title>
            </Helmet>

            {currentStepObj && currentStepObj.step === 1 &&
                <div className="Digital">
                    {currentStepObj.component}
                </div>
            }
            {currentStepObj && currentStepObj.step !== 1 &&
                <HeaderProgressTracker title={currentStepObj.title} hidden={currentStepObj.hidden} showContainer={currentStepObj.showContainer} hideProgressTracker={currentStepObj.hideProgressTracker} headerProductsText={true} stepName={currentStepObj.progressTitle} step={currentStepObj.categoryNumber} totalSteps={3} whiteBg={currentStepObj.whiteBackground} fullWidth={currentStepObj.fullWidth}>
                    <div className="Digital">
                        {currentStepObj.component}
                    </div>
                </HeaderProgressTracker>
            }
        </div>
    );
}
